import { PointerEvent, useEffect, useMemo, useState } from "react";
import { PlayModule } from "./PlayModule";
import { makeAutonomousPilot } from "./AutonomousPilot";
import { PadControl, TextureBind, TextureUpload } from "./GeniePlayBridge";

const formatExecutable = (name: string) => name || "(waiting for game boot)";

const textureLabel = (texture: TextureBind) =>
  `0x${texture.tbp0.toString(16).toUpperCase().padStart(4, "0")} ${texture.width}×${texture.height}`;

const uploadLabel = (upload: TextureUpload) =>
  `DBP 0x${upload.dbp.toString(16).toUpperCase().padStart(4, "0")} ${upload.width}×${upload.height} PSM ${upload.dpsm} DBW ${upload.dbw} FP 0x${(upload.fingerprint ?? 0).toString(16).toUpperCase().padStart(4, "0")}`;

const uploadKey = (upload: TextureUpload) =>
  `${upload.dbp}:${upload.dbw}:${upload.dpsm}:${upload.width}:${upload.height}:${upload.fingerprint ?? 0}`;

const subtractUploads = (source: TextureUpload[], comparison: TextureUpload[]) => {
  const remaining = new Map<string, number>();
  for (const upload of comparison) {
    const key = uploadKey(upload);
    remaining.set(key, (remaining.get(key) ?? 0) + 1);
  }

  return source.filter((upload) => {
    const key = uploadKey(upload);
    const count = remaining.get(key) ?? 0;
    if (count === 0) return true;
    if (count === 1) remaining.delete(key);
    else remaining.set(key, count - 1);
    return false;
  });
};

const sleep = (milliseconds: number) =>
  new Promise<void>((resolve) => window.setTimeout(resolve, milliseconds));

const DIAGNOSTIC_STATE_KEY = "geniewoman.mm2.diagnostic-state.v1";

type PartProbe = {
  label: string;
  added: TextureUpload[];
  removed: TextureUpload[];
  baselineCount: number;
  variantCount: number;
};

type PersistedDiagnosticState = {
  textureCandidates: TextureBind[];
  partProbes: PartProbe[];
  updatedAt: string;
};

const loadDiagnosticState = (): PersistedDiagnosticState | null => {
  try {
    const raw = window.localStorage.getItem(DIAGNOSTIC_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedDiagnosticState>;
    if (!Array.isArray(parsed.textureCandidates) || !Array.isArray(parsed.partProbes)) return null;
    return {
      textureCandidates: parsed.textureCandidates as TextureBind[],
      partProbes: parsed.partProbes as PartProbe[],
      updatedAt: typeof parsed.updatedAt === "string" ? parsed.updatedAt : "",
    };
  } catch {
    return null;
  }
};

type TouchButtonProps = {
  label: string;
  control: PadControl;
  className?: string;
  onDown: (control: PadControl) => void;
  onUp: (control: PadControl) => void;
};

function TouchButton({ label, control, className = "", onDown, onUp }: TouchButtonProps) {
  const press = (event: PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    onDown(control);
  };

  const release = (event: PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    onUp(control);
  };

  return (
    <button
      type="button"
      className={`touch-key ${className}`.trim()}
      aria-label={label}
      onPointerDown={press}
      onPointerUp={release}
      onPointerCancel={release}
      onPointerLeave={(event) => {
        if (event.buttons !== 0) release(event);
      }}
    >
      {label}
    </button>
  );
}

export default function Mm2ControlPanel() {
  const tools = useMemo(() => {
    if (PlayModule === null) return null;
    return makeAutonomousPilot(PlayModule);
  }, []);
  const persisted = useMemo(() => loadDiagnosticState(), []);

  const [executable, setExecutable] = useState("");
  const [status, setStatus] = useState(
    persisted?.updatedAt
      ? `Bridge ready. Restored persistent diagnostic map from ${new Date(persisted.updatedAt).toLocaleString()}.`
      : "Bridge ready.",
  );
  const [busy, setBusy] = useState(false);
  const [textureCandidates, setTextureCandidates] = useState<TextureBind[]>(persisted?.textureCandidates ?? []);
  const [partProbes, setPartProbes] = useState<PartProbe[]>(persisted?.partProbes ?? []);
  const held = useMemo(() => new Set<PadControl>(), []);

  useEffect(() => {
    try {
      const state: PersistedDiagnosticState = {
        textureCandidates,
        partProbes,
        updatedAt: new Date().toISOString(),
      };
      window.localStorage.setItem(DIAGNOSTIC_STATE_KEY, JSON.stringify(state));
    } catch {
      // Persistence is best-effort. The live bridge must continue even when storage is unavailable.
    }
  }, [textureCandidates, partProbes]);

  useEffect(() => {
    if (!tools) return;
    const update = () => {
      try {
        setExecutable(tools.bridge.executableName());
      } catch {
        setExecutable("");
      }
    };
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [tools]);

  if (!tools) {
    return <section className="mm2-panel">Bridge is initializing…</section>;
  }

  const controlDown = (control: PadControl) => {
    if (held.has(control)) return;
    held.add(control);
    try {
      tools.bridge.down(control);
    } catch (error) {
      held.delete(control);
      setStatus(`Control ${control} failed: ${String(error)}`);
    }
  };

  const controlUp = (control: PadControl) => {
    if (!held.has(control)) return;
    held.delete(control);
    try {
      tools.bridge.up(control);
    } catch (error) {
      setStatus(`Release ${control} failed: ${String(error)}`);
    }
  };

  const releaseAll = () => {
    for (const control of Array.from(held)) controlUp(control);
  };

  const run = async (label: string, action: () => Promise<void>) => {
    if (busy) return;
    releaseAll();
    setBusy(true);
    setStatus(`${label} running…`);
    try {
      await action();
      setStatus(`${label} complete.`);
    } catch (error) {
      setStatus(`${label} failed: ${String(error)}`);
    } finally {
      setBusy(false);
    }
  };

  const scanCharacterTextures = async () => {
    if (busy) return;
    releaseAll();
    setBusy(true);
    setTextureCandidates([]);
    setPartProbes([]);
    setStatus("Texture spy is watching the character on screen…");
    try {
      const scan = await tools.bridge.scanTextureBinds(1200);
      const likelyCharacterTextures = scan.characterBankCandidates.filter(
        (texture) => texture.psm === 0x14 || texture.psm === 0x13,
      );
      setTextureCandidates(likelyCharacterTextures);
      setStatus(
        `Texture spy saw ${scan.binds.length} active texture bindings and found ${likelyCharacterTextures.length} likely character pieces. Persistent map updated.`,
      );
    } catch (error) {
      setStatus(`Texture scan failed: ${String(error)}`);
    } finally {
      setBusy(false);
    }
  };

  const autoIdentifyPlayerParts = async () => {
    if (busy) return;
    releaseAll();
    setBusy(true);
    setTextureCandidates([]);
    setPartProbes([]);

    const labels = ["Character", "Hair Style", "Hair Color", "Face"];
    const results: PartProbe[] = [];

    try {
      setStatus("Payload-aware player probe starting. Leave the four-row Character Setting menu open with Character selected and do not touch the controls…");
      await sleep(400);

      for (let row = 0; row < labels.length; row += 1) {
        const label = labels[row];
        setStatus(`Payload probe ${row + 1}/4: mapping ${label} GS upload bytes…`);

        const baselineScan = await tools.bridge.scanTextureUploads(700);
        const baseline = baselineScan.characterBankCandidates;

        // Arm the tracer before the menu value changes. Some customization assets
        // upload only during the transition and disappear before a post-change scan.
        const variantScanPromise = tools.bridge.scanTextureUploads(1000);
        await sleep(120);
        await tools.bridge.tap("DPAD_RIGHT", 90);
        const variantScan = await variantScanPromise;
        const variant = variantScan.characterBankCandidates;

        // Compare captures as multisets of destination geometry plus payload
        // fingerprint. This exposes swaps that reuse the same GS address, size,
        // and number of transfers but carry different character or palette bytes.
        const added = subtractUploads(variant, baseline);
        const removed = subtractUploads(baseline, variant);

        results.push({
          label,
          added,
          removed,
          baselineCount: baseline.length,
          variantCount: variant.length,
        });
        setPartProbes(results.slice());

        await tools.bridge.tap("DPAD_LEFT", 90);
        await sleep(450);

        if (row < labels.length - 1) {
          await tools.bridge.tap("DPAD_DOWN", 90);
          await sleep(250);
        }
      }

      for (let i = 0; i < 3; i += 1) {
        await tools.bridge.tap("DPAD_UP", 90);
        await sleep(120);
      }

      const totalChanged = results.reduce(
        (sum, result) => sum + result.added.length + result.removed.length,
        0,
      );
      setStatus(
        totalChanged > 0
          ? `Payload probe complete. I isolated ${totalChanged} changed GS upload events across the four player settings and saved the fingerprinted map locally.`
          : "Payload probe found no distinguishable change inside the captured character bank. Do not guess offsets; the next step is to repeat/correlate outside this VRAM bank or trace the source package read path.",
      );
    } catch (error) {
      setStatus(`Payload-aware player-part probe failed: ${String(error)}`);
      releaseAll();
    } finally {
      setBusy(false);
    }
  };

  const installDebugItem = () => {
    try {
      tools.bridge.installJapaneseDebugMenuItem();
      setStatus("Japanese MM2 Debug Mode item 0x0D installed in menu slot 11.");
    } catch (error) {
      setStatus(`Debug item refused: ${String(error)}`);
    }
  };

  const clearPersistentMap = () => {
    try {
      window.localStorage.removeItem(DIAGNOSTIC_STATE_KEY);
    } catch {
      // Keep the UI responsive if storage is blocked.
    }
    setTextureCandidates([]);
    setPartProbes([]);
    setStatus("Persistent MM2 diagnostic map cleared.");
  };

  const japaneseMm2 = tools.bridge.isJapaneseMm2();
  const key = (label: string, control: PadControl, className = "") => (
    <TouchButton
      label={label}
      control={control}
      className={className}
      onDown={controlDown}
      onUp={controlUp}
    />
  );

  return (
    <section className="mm2-panel">
      <div className="mm2-panel__header">
        <div>
          <strong>GenieWoman MM2 Bridge</strong>
          <div className="mm2-panel__sub">External Navi control of the emulated PS2 player body</div>
        </div>
        <span className="mm2-panel__badge">LIVE BRIDGE</span>
      </div>

      <dl className="mm2-panel__readout">
        <div><dt>Executable</dt><dd>{formatExecutable(executable)}</dd></div>
        <div><dt>EE RAM</dt><dd>{(tools.bridge.eeRamSize() / 1024 / 1024).toFixed(0)} MiB address window</dd></div>
        <div><dt>Status</dt><dd>{status}</dd></div>
      </dl>

      <div className="touch-controller" onContextMenu={(event) => event.preventDefault()}>
        <div className="touch-controller__group">
          <div className="touch-controller__label">D-Pad</div>
          <div className="touch-cross">
            <span />{key("▲", "DPAD_UP")}<span />
            {key("◀", "DPAD_LEFT")}<span className="touch-cross__center" />{key("▶", "DPAD_RIGHT")}
            <span />{key("▼", "DPAD_DOWN")}<span />
          </div>
        </div>

        <div className="touch-controller__group touch-controller__middle">
          <div className="touch-controller__label">System</div>
          <div className="touch-system">
            {key("Select", "SELECT", "touch-key--small")}
            {key("Start", "START", "touch-key--small")}
          </div>
          <div className="touch-controller__label touch-controller__label--lower">Left Stick</div>
          <div className="touch-cross touch-cross--small">
            <span />{key("↑", "LEFT_STICK_UP")}<span />
            {key("←", "LEFT_STICK_LEFT")}<span className="touch-cross__center" />{key("→", "LEFT_STICK_RIGHT")}
            <span />{key("↓", "LEFT_STICK_DOWN")}<span />
          </div>
        </div>

        <div className="touch-controller__group">
          <div className="touch-controller__label">Face</div>
          <div className="touch-face">
            <span />{key("△", "TRIANGLE", "touch-key--triangle")}<span />
            {key("□", "SQUARE", "touch-key--square")}<span />{key("○", "CIRCLE", "touch-key--circle")}
            <span />{key("×", "CROSS", "touch-key--cross")}<span />
          </div>
        </div>
      </div>

      <div className="touch-shoulders">
        {key("L1", "L1", "touch-key--shoulder")}
        {key("L2", "L2", "touch-key--shoulder")}
        <span />
        {key("R2", "R2", "touch-key--shoulder")}
        {key("R1", "R1", "touch-key--shoulder")}
      </div>

      <div className="mm2-panel__buttons">
        <button disabled={busy} onClick={autoIdentifyPlayerParts}>🧞‍♀️ Payload Map Player Parts</button>
        <button disabled={busy} onClick={scanCharacterTextures}>🔎 Scan Character Textures</button>
        <button disabled={busy} onClick={() => run("Forward step", () => tools.bridge.walkForward(600))}>Walk Forward</button>
        <button disabled={busy} onClick={() => run("Autonomous smoke test", () => tools.pilot.smokeTest())}>Genie Smoke Test</button>
        <button disabled={busy} onClick={() => run("Square patrol", () => tools.pilot.patrolSquare())}>Autonomous Patrol</button>
        <button
          disabled={!japaneseMm2 || busy}
          title={japaneseMm2 ? "Install hidden Debug Mode item" : "Requires Japanese SLPS-20469 build"}
          onClick={installDebugItem}
        >
          Install JP Debug Item
        </button>
        <button disabled={busy} onClick={clearPersistentMap}>Clear Persistent Map</button>
        <button onClick={() => {
          tools.pilot.stop();
          releaseAll();
          setStatus("Autonomous pilot stop requested; virtual controls released.");
        }}>
          Stop / Release All
        </button>
      </div>

      {partProbes.length > 0 && (
        <div className="texture-scan-card">
          <strong>🧞‍♀️ Player-part GS payload map · persistent</strong>
          <p>I arm the GS tracer before each customization change, fingerprint the actual host payload bytes, preserve repeated same-address transfers, restore the original value, and keep the differential map across browser reloads.</p>
          {partProbes.map((probe) => (
            <div key={probe.label} style={{ marginTop: "0.7rem" }}>
              <strong>{probe.label}</strong>
              <div>
                {probe.added.length + probe.removed.length === 0
                  ? `No fingerprinted upload change (${probe.baselineCount}→${probe.variantCount}). Do not infer a mapping from this transition.`
                  : `Changed fingerprinted upload events: +${probe.added.length} / −${probe.removed.length}`}
              </div>
              {probe.added.length > 0 && <code>New: {probe.added.map(uploadLabel).join(" · ")}</code>}
              {probe.removed.length > 0 && <code>Old: {probe.removed.map(uploadLabel).join(" · ")}</code>}
            </div>
          ))}
        </div>
      )}

      {textureCandidates.length > 0 && (
        <div className="texture-scan-card">
          <strong>🧞‍♀️ Character pieces spotted: {textureCandidates.length} · persistent</strong>
          <p>These are active texture bindings in MM2's known character VRAM bank.</p>
          <code>{textureCandidates.map(textureLabel).join(" · ")}</code>
        </div>
      )}

      <p className="mm2-panel__note">
        Touch controls are press-and-hold, so menus and movement work like a controller rather than one-shot buttons.
        Payload Map expects the four-row Character Setting menu to be open with Character selected at the top. It restores every option after testing it.
        The player mapper arms the GS tracer before each value changes and compares destination geometry, repeated transfers, and a compact fingerprint of the actual uploaded bytes, so same-address and same-count hair/body swaps can finally separate without guessing binary offsets.
      </p>
    </section>
  );
}